import type SB from '.';
import { unreachable } from '../utils/Utils';

/** @returns Validation error message or `null` in case of passed validation. */
export type PrecompiledValidator = (val: unknown) => string | null;

export function precompileValidator(schema: SB.Schema): PrecompiledValidator {
    const argName = 'val';

    const source = `${generateValidator(schema, argName)}\nreturn null;`;

    const validator = new Function(argName, source); // eslint-disable-line @typescript-eslint/no-implied-eval

    return validator as PrecompiledValidator;
}

function generateValidator(
    schema: SB.Schema<any>,
    argName: string,
    depth = 0,
    makeError = (cause: string): string => cause,
): string {
    let res = '';

    switch (schema._type) {
        case 'array': {
            const itemSchema = (schema as SB.ArraySchema<any>).getItemSchema();

            res += `if (!Array.isArray(${argName})) return \`${makeError(`Not an array \${${argName}}`)}\`;\n`;

            const { minLengthVal, maxLengthVal } = schema as any;

            if (minLengthVal === undefined || maxLengthVal === undefined) {
                throw Error('Missing minLengthVal or maxLengthVal property in array schema');
            }

            if (minLengthVal > 0 || maxLengthVal < Infinity) {
                const error = makeError(
                    `Length out of range: \${${argName}.length} in range [${minLengthVal}, ${maxLengthVal}]`,
                );
                res += `if (${minLengthVal} > ${argName}.length || ${maxLengthVal} < ${argName}.length) return \`${error}\`;\n`;
            }

            if (itemSchema._type !== 'unknown') {
                const i = `i${depth}`;

                const itemVar = `item${depth}_i`;

                const itemValidator = generateValidator(itemSchema, itemVar, depth + 1, (cause) =>
                    makeError(`Error validating item \${${i}}: ${cause}`),
                );

                const iteratorBody = `const ${itemVar} = ${argName}[${i}] ?? undefined;\n${itemValidator}`; // If we've stringified the val, we may get nulls here. Convert to undefined to pass validation.`

                res += `for (let ${i} = 0; ${i} < ${argName}.length; ${i}++) {\n  ${iteratorBody}}\n`;
            }

            break;
        }
        case 'boolean': {
            res = `if (typeof ${argName} !== 'boolean') return '${makeError('Not a boolean')}';\n`;

            break;
        }
        case 'integer': {
            res += `if (!Number.isInteger(${argName})) return '${makeError('Not an integer')}';\n`;

            res += generateNumberRangeValidator(schema as SB.IntegerSchema, argName, makeError);

            break;
        }
        case 'map': {
            const itemSchema = (schema as any as SB.MapSchema<any>).getItemSchema();

            res += `if (typeof ${argName} !== 'object' || ${argName} === null) return \`${makeError(
                `Not a map: \${${argName}}`,
            )}\`;\n`;

            if (itemSchema._type !== 'unknown') {
                const key = `key${depth}`;

                const itemValidator = generateValidator(itemSchema, `${argName}[${key}]`, depth + 1, (cause) =>
                    makeError(`Error validating item in map with key "${key}": ${cause}`),
                );

                res += `for (const ${key} in ${argName}) {\n  ${itemValidator}}\n`;
            }

            break;
        }
        case 'null': {
            res = `if (${argName} !== null) return '${makeError('Not null')}';\n`;

            break;
        }
        case 'number': {
            res += `if (typeof ${argName} !== 'number') return \`${makeError(`Not a number: \${${argName}}`)}\`;\n`;

            res += generateNumberRangeValidator(schema as SB.NumberSchema, argName, makeError);

            res += `if (isNaN(${argName})) return \`${makeError('Value is NaN')}\`;\n`;

            break;
        }
        case 'object': {
            if (!schema.isObjectSchema()) {
                throw Error(`Unexpected object schema: ${JSON.stringify(schema)}`);
            }

            res += `if (typeof ${argName} !== 'object' || ${argName} === null) return \`${makeError(
                `Not an object: \${${argName}}`,
            )}\`;\n`;

            const propertySchemas = schema.getFullSchema() as Record<string, SB.Schema>;
            const additionalPropertiesSchema = schema.getAdditionalPropertyValuesSchema();

            // Keep track of missing properties:
            const requiredProps = Object.keys(propertySchemas).filter((prop) => propertySchemas[prop]!.required);
            const hasPropVar = (prop: string): string => `has${depth}_${prop}`;
            if (requiredProps.length > 0) {
                res += `let ${requiredProps.map((prop) => `${hasPropVar(prop)}=false`).join(',')};\n`;
            }

            // Validate properties:
            const key = `key${depth}`;
            let keyIndex = 0;
            let iteratorBody = '';

            for (const prop in propertySchemas) {
                const propSchema = propertySchemas[prop]!;

                let conditionBody = generateValidator(propSchema, `${argName}[${key}]`, depth + 1, (cause) =>
                    makeError(`Error validating field "${prop}": ${cause}`),
                );

                if (propSchema.required) {
                    conditionBody += `if (typeof ${argName}[${key}] !== 'undefined') ${hasPropVar(prop)}=true;\n`;
                }

                const ifOperator = keyIndex++ === 0 ? 'if' : 'else if';

                iteratorBody += `${ifOperator} (${key} === '${prop}') {\n  ${conditionBody}}\n`;
            }

            // Validate additional properties:
            const hasPropertySchemas = Object.keys(propertySchemas).length === 0;

            if (additionalPropertiesSchema && additionalPropertiesSchema._type !== 'unknown') {
                const additionalPropertiesValidator = generateValidator(
                    additionalPropertiesSchema,
                    `${argName}[${key}]`,
                    depth + 1,
                    (cause) => makeError(`Error validating field "\${${key}}": ${cause}`),
                );

                if (hasPropertySchemas) {
                    iteratorBody += additionalPropertiesValidator;
                } else {
                    iteratorBody += `else {\n  ${additionalPropertiesValidator}}\n`;
                }
            } else if (!additionalPropertiesSchema) {
                const error = makeError(`Unexpected field: \${${key}}`);

                if (hasPropertySchemas) {
                    iteratorBody += `return \`${error}\`;\n`;
                } else {
                    iteratorBody += `else { return \`${error}\`; }\n`;
                }
            }

            res += `for (const ${key} in ${argName}) {\n  ${iteratorBody}\n}\n`;

            // Check for missing properties:
            if (requiredProps.length > 0) {
                const hasPropConditions = requiredProps.map((prop) => hasPropVar(prop)).join(' && ');
                const missingFieldsArrayItems = requiredProps
                    .map((prop) => `${hasPropVar(prop)} ? undefined : '${prop}'`)
                    .join(',');
                const missingFields = `[${missingFieldsArrayItems}].filter(item => !!item).join(', ')`;

                const error = makeError(`Missing fields: \${${missingFields}}`);

                res += `if (!(${hasPropConditions})) return \`${error}\`;\n`;
            }

            break;
        }
        case 'string': {
            res = `if (typeof ${argName} !== 'string') return \`${makeError(`Not a string: \${${argName}}`)}\`;\n`;

            break;
        }
        case 'tuple': {
            const { values } = schema as any as { values: string[] };

            if (values === undefined) {
                throw Error('Missing values property in tuple schema');
            }

            const conditions = values.map((value) => `${argName} !== '${value}'`).join(' && ');

            const error = makeError(`\${${argName}} is not valid, possible values: ${values.join(', ')}`);

            res = `if (${conditions}) return \`${error}\`;\n`;

            break;
        }
        case 'union': {
            const itemSchemas = (schema as any as SB.UnionSchema<any>).getItemSchemas();

            const errorVar = `union${depth}_errors`;
            const resultVar = `union${depth}_result`;

            res += `let ${errorVar} = '', ${resultVar};\n`;

            for (const itemSchema of itemSchemas) {
                const itemValidator = generateValidator(
                    itemSchema,
                    argName,
                    depth,
                    (cause) => `Could not validate value as ${itemSchema.constructor.name}: ${cause}`,
                );

                res += `${resultVar} = (() => {\n  ${itemValidator}})();\n`;
                res += `if (${resultVar}) { ${errorVar} += (${errorVar} ? '\\n' : '') + ${resultVar} } else { return null; }\n`;
            }

            const error = makeError(`\${${errorVar}}`);
            res += `return \`${error}\`;\n`;

            break;
        }
        case 'unknown': {
            // Validates everything

            break;
        }
        default: {
            unreachable(schema._type);
        }
    }

    return schema.required || res === '' ? res : `if (${argName} !== undefined) {\n  ${res}}\n`;
}

function generateNumberRangeValidator(
    schema: SB.NumberSchema,
    argName: string,
    makeError: (cause: string) => string,
): string {
    const { minVal, maxVal } = schema as any as { minVal: number; maxVal: number };

    if (minVal === undefined || maxVal === undefined) {
        throw Error('Missing minVal or maxVal in number schema');
    }

    if (minVal > -Infinity || maxVal < Infinity) {
        const error = makeError(`Out of range: \${${argName}} in range [${minVal}, ${maxVal}]`);

        return `if (${minVal} > ${argName} || ${maxVal} < ${argName}) return \`${error}\`;\n`;
    }

    return '';
}
